import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMessageSquare, FiChevronDown } from 'react-icons/fi';
import { NewChatPicker } from '../chat/NewChatPicker';
import { personaChatPath } from '../../utils/personaChatPath';
import type { Persona } from '../../types/persona';

type Variant = 'primary' | 'ghost';

export interface NewConversationButtonProps {
  variant?: Variant;
  className?: string;
  /**
   * Escape hatch: host renders its own trigger chrome. When set, `variant`/`className`
   * are IGNORED, and the host MUST render a focusable control with
   * `aria-haspopup="listbox"` + `aria-expanded={isOpen}` (the component cannot inject
   * ARIA into arbitrary host markup).
   */
  renderTrigger?: (args: { open: () => void; isOpen: boolean }) => React.ReactNode;
  /** When set: direct-launch mode — renders an <a> (Link), no picker, no popup ARIA. */
  persona?: Persona;
  initialSelectedTags?: string[];
}

const VARIANT_CLASS: Record<Variant, string> = {
  primary:
    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white ' +
    'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 ' +
    'shadow-lg hover:shadow-xl transition-all duration-200 motion-safe:hover:-translate-y-0.5',
  // green-700 (not 600): white text on green-600 ≈3.4:1 fails WCAG 1.4.3 (a11y triad). min-h-[40px]: touch target.
  ghost:
    'inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 py-1.5 rounded-md text-xs font-semibold ' +
    'text-white bg-green-700 hover:bg-green-600 dark:bg-green-700 dark:hover:bg-green-600 transition-colors',
};

export const NewConversationButton: React.FC<NewConversationButtonProps> = ({
  variant = 'primary', className = '', renderTrigger, persona, initialSelectedTags,
}) => {
  const navigate = useNavigate();

  // ── Direct-launch mode: real link, no popup ARIA (a11y-B1/B2). ──
  if (persona) {
    return (
      <Link
        to={personaChatPath(persona.id)}
        onClick={(e) => e.stopPropagation()}  // don't trip a click-through host card (architect-B3)
        className={`${VARIANT_CLASS[variant]} ${className}`}
      >
        <FiMessageSquare aria-hidden="true" className="shrink-0 w-4 h-4" />
        <span className="truncate" title={`Chat with ${persona.name}`}>Chat with {persona.name}</span>
      </Link>
    );
  }

  // ── Picker mode: button + popup ARIA. Route lives in personaChatPath, so the
  //    picker's internal navigate fallback is bypassed (B will delete it). ──
  const handleSelect = (p: Persona) => { navigate(personaChatPath(p.id)); };

  return (
    <NewChatPicker
      onSelect={handleSelect}
      initialSelectedTags={initialSelectedTags}
      trigger={({ open, isOpen }) =>
        renderTrigger ? renderTrigger({ open, isOpen }) : (
          <button
            type="button"
            onClick={open}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            data-testid="new-conversation-trigger"
            className={`${VARIANT_CLASS[variant]} ${className}`}
          >
            <FiMessageSquare aria-hidden="true" className="w-4 h-4" />
            <span>New Conversation</span>
            <FiChevronDown aria-hidden="true"
              className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )
      }
    />
  );
};
